import { Box, Card, CardContent, Typography } from '@mui/material'
import { SelectInput } from 'components'
import { t } from 'i18n'
import React, { useState } from 'react'
import { HeatmapDefinition } from 'store/ducks/report/types'
import HeatmapActions from './HeatmapActions'
import HeatmapPreview from './HeatmapPreview'

interface HeatmapCardProps {
  season: string
  heatmaps: HeatmapDefinition[]
  selectedHeatmapId?: string
  onHeatmapSelection: (season: string, heatmapId: string) => void
  onSetDefault: (heatmapId: string) => Promise<void>
  onReplace: (heatmap: HeatmapDefinition) => Promise<void>
  onRename: (heatmap: HeatmapDefinition) => void
  onDelete: (heatmap: HeatmapDefinition) => Promise<void>
  onPreviewClick: (heatmap: HeatmapDefinition) => void
}

const HeatmapCard: React.FC<HeatmapCardProps> = ({
  season,
  heatmaps,
  selectedHeatmapId,
  onHeatmapSelection,
  onSetDefault,
  onReplace,
  onRename,
  onDelete,
  onPreviewClick
}) => {
  const [replacingId, setReplacingId] = useState<string | null>(null)

  const selectedHeatmap = heatmaps.find(h => h.id === selectedHeatmapId)

  const heatmapOptions = heatmaps.map(h => ({
    id: h.id,
    label: `${h.name || h.fileName}${h.isDefault ? ' (Default)' : ''}`
  }))

  const handleReplace = async (heatmap: HeatmapDefinition) => {
    setReplacingId(heatmap.id)
    try {
      await onReplace(heatmap)
    } finally {
      setReplacingId(null)
    }
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="h3">
            {season.toLowerCase() === 'winter' ? '☃️' : '🌞'}{' '}
            {t(season.toLowerCase()) || season}{' '}
            {t('heatmaps.title') || 'Heatmaps'}
          </Typography>
        </Box>

        {/* Dropdown to select which heatmap to manage */}
        <SelectInput
          name={`heatmap-${season}`}
          label={t('heatmaps.select') || 'Select Heatmap'}
          value={selectedHeatmapId || ''}
          onChange={(e: any) => onHeatmapSelection(season, e.target.value)}
          options={heatmapOptions}
          translateText={false}
        />

        {/* Show selected heatmap details and actions */}
        {selectedHeatmap && (
          <>
            <Box mt={1}>
              <HeatmapPreview
                heatmap={selectedHeatmap}
                previewUrl={selectedHeatmap.url}
                onClick={() => {
                  if (selectedHeatmap.url) onPreviewClick(selectedHeatmap)
                }}
              />
            </Box>

            {selectedHeatmap.fileDate && (
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
              >
                Date: {new Date(selectedHeatmap.fileDate).toLocaleDateString()}
              </Typography>
            )}

            <HeatmapActions
              heatmap={selectedHeatmap}
              isReplacing={replacingId === selectedHeatmap.id}
              onSetDefault={onSetDefault}
              onReplace={handleReplace}
              onRename={onRename}
              onDelete={onDelete}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default HeatmapCard
